/**
 * IT Invoice Submission: a month-by-month tracker of whether each vendor of
 * each office location has submitted its invoice.
 *
 * Office locations come from the existing `office_locations` master list
 * (Admin > Settings > Office Locations) — this module never keeps its own
 * office list. Vendors belong to one office; a vendor x month cell has no
 * stored row until someone sets it, and no row means NOT_SUBMITTED.
 *
 * Everything here is shared by the API (validation), the worker (reminder
 * schedule and email) and the web app (month columns, forms), so the three
 * cannot drift apart.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------
export const INVOICE_SUBMISSION_STATUSES = ['NOT_SUBMITTED', 'SUBMITTED'];
/** What a vendor x month cell means when nothing has been stored for it. */
export const DEFAULT_INVOICE_SUBMISSION_STATUS = 'NOT_SUBMITTED';
export const INVOICE_STATUS_LABELS = {
    NOT_SUBMITTED: 'Invoice Not Submitted',
    SUBMITTED: 'Bill Submitted',
};
// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------
export const INVOICE_VENDOR_NAME_MAX = 120;
export const INVOICE_CIRCUIT_ACCOUNT_ID_MAX = 100;
/** How many vendors an office starts with the first time the module sees it. */
export const INVOICE_DEFAULT_VENDOR_COUNT = 3;
export const CIRCUIT_ACCOUNT_ID_LABEL = 'Circuit ID / Account ID';
/** "Vendor 1", "Vendor 2", "Vendor 3" — never a generated Circuit ID. */
export function defaultInvoiceVendorName(slot) {
    return `Vendor ${slot}`;
}
// Control characters (incl. newlines/tabs) are refused: these values are shown
// in one-line table cells and in plain-text email.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;
export const invoiceVendorNameSchema = z
    .string({ required_error: 'Vendor name is required.', invalid_type_error: 'Vendor name is required.' })
    .max(INVOICE_VENDOR_NAME_MAX * 4, `Use ${INVOICE_VENDOR_NAME_MAX} characters or fewer.`)
    .transform((v) => v.trim())
    .pipe(z
    .string()
    .min(1, 'Vendor name is required.')
    .max(INVOICE_VENDOR_NAME_MAX, `Use ${INVOICE_VENDOR_NAME_MAX} characters or fewer.`)
    .refine((v) => !CONTROL_CHARS.test(v), 'Vendor name cannot contain line breaks or control characters.'));
/**
 * Optional free text: letters, digits, spaces, '-', '/', '.', ... are all
 * fine (no numeric-only or format rule exists). Trimmed; blank or
 * whitespace-only becomes null ("no Circuit ID / Account ID").
 */
export const invoiceCircuitAccountIdSchema = z
    .string()
    .max(INVOICE_CIRCUIT_ACCOUNT_ID_MAX * 4, `Use ${INVOICE_CIRCUIT_ACCOUNT_ID_MAX} characters or fewer.`)
    .nullish()
    .transform((v) => (v == null || v.trim() === '' ? null : v.trim()))
    .pipe(z
    .string()
    .max(INVOICE_CIRCUIT_ACCOUNT_ID_MAX, `Use ${INVOICE_CIRCUIT_ACCOUNT_ID_MAX} characters or fewer.`)
    .refine((v) => !CONTROL_CHARS.test(v), 'Circuit ID / Account ID cannot contain line breaks or control characters.')
    .nullable());
export const createInvoiceVendorDtoSchema = z
    .object({
    name: invoiceVendorNameSchema,
    circuitAccountId: invoiceCircuitAccountIdSchema.optional(),
})
    .strict();
/** PATCH: only the fields sent change. `circuitAccountId: null` (or blank) clears it. */
export const updateInvoiceVendorDtoSchema = z
    .object({
    name: invoiceVendorNameSchema.optional(),
    circuitAccountId: invoiceCircuitAccountIdSchema.optional(),
    isActive: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to update.' });
// ---------------------------------------------------------------------------
// Months
// ---------------------------------------------------------------------------
export const INVOICE_YEAR_MIN = 2000;
export const INVOICE_YEAR_MAX = 2100;
/** Upper bound on how many month columns one request may ask for. */
export const INVOICE_MONTHS_MAX = 24;
export const INVOICE_MONTHS_DEFAULT = 12;
const yearSchema = z.coerce.number().int().min(INVOICE_YEAR_MIN).max(INVOICE_YEAR_MAX);
const monthSchema = z.coerce.number().int().min(1).max(12);
/** "2026-10" — the key month columns and cells are addressed by. */
export function invoiceMonthKey(ym) {
    return `${ym.year}-${String(ym.month).padStart(2, '0')}`;
}
/** `ym` shifted by `delta` months (negative goes back), across year boundaries. */
export function addMonths(ym, delta) {
    const index = ym.year * 12 + (ym.month - 1) + delta;
    return { year: Math.floor(index / 12), month: (((index % 12) + 12) % 12) + 1 };
}
/** `count` consecutive months starting at `start` (chronological, December rolls into January). */
export function monthSequence(start, count) {
    return Array.from({ length: Math.max(0, count) }, (_, i) => addMonths(start, i));
}
export function compareYearMonth(a, b) {
    return a.year * 12 + a.month - (b.year * 12 + b.month);
}
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** "October 2026" (or "Oct 2026" with `short`). Locale-independent. */
export function formatYearMonth(ym, opts = {}) {
    const name = MONTH_NAMES[ym.month - 1] ?? '';
    return `${opts.short ? name.slice(0, 3) : name} ${ym.year}`;
}
/** Wall-clock calendar parts of `instant` in IANA zone `timeZone`. */
export function zonedParts(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23',
        weekday: 'short',
    }).formatToParts(instant);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return {
        year: Number(get('year')),
        month: Number(get('month')),
        day: Number(get('day')),
        hour: Number(get('hour')) % 24,
        minute: Number(get('minute')),
        weekday: weekdays.indexOf(get('weekday')) + 1,
    };
}
/** The current calendar month in the application time zone (never the browser's or the server's own zone). */
export function currentYearMonth(now, timeZone) {
    const p = zonedParts(now, timeZone);
    return { year: p.year, month: p.month };
}
export const setInvoiceSubmissionDtoSchema = z
    .object({
    year: z.number().int().min(INVOICE_YEAR_MIN).max(INVOICE_YEAR_MAX),
    month: z.number().int().min(1).max(12),
    status: z.enum(INVOICE_SUBMISSION_STATUSES),
})
    .strict();
/**
 * `GET /invoice-submission` query. With no `fromYear`/`fromMonth` the window
 * starts at the CURRENT month (application time zone). `includeInactive`
 * also returns inactive offices (that have vendors) and inactive vendors, for
 * history.
 */
export const invoiceMatrixQuerySchema = z
    .object({
    fromYear: yearSchema.optional(),
    fromMonth: monthSchema.optional(),
    months: z.coerce.number().int().min(1).max(INVOICE_MONTHS_MAX).default(INVOICE_MONTHS_DEFAULT),
    includeInactive: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
})
    .strict()
    .refine((v) => (v.fromYear === undefined) === (v.fromMonth === undefined), { message: 'Send both fromYear and fromMonth, or neither.', path: ['fromMonth'] });
// ---------------------------------------------------------------------------
// Reminder email settings and schedule
// ---------------------------------------------------------------------------
export const INVOICE_REMINDER_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY'];
/** ISO weekday numbers, Monday = 1 ... Sunday = 7. */
export const ISO_WEEKDAY_LABELS = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
};
/** `system_settings` key that holds the single reminder configuration (`StoredInvoiceReminderSettings`). */
export const INVOICE_REMINDER_SETTINGS_KEY = 'invoice.reminder_settings';
export const INVOICE_REMINDER_DEFAULT_TIME = '09:00';
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;
const EMAIL_MAX = 254;
/** The administrator-editable part of the reminder configuration. */
export const invoiceReminderSettingsSchema = z
    .object({
    enabled: z.boolean(),
    recipient: z
        .string()
        .max(EMAIL_MAX * 2)
        .nullish()
        .transform((v) => (v == null || v.trim() === '' ? null : v.trim().toLowerCase()))
        .pipe(z.string().max(EMAIL_MAX, 'Email address is too long.').email('Enter a valid email address.').nullable()),
    frequency: z.enum(INVOICE_REMINDER_FREQUENCIES),
    timeOfDay: z.string().regex(TIME_OF_DAY, 'Use a 24-hour time such as 09:00.'),
    /** Used when frequency is WEEKLY (ISO weekday, Monday = 1). Always stored so switching back keeps it. */
    weekday: z.number().int().min(1).max(7),
    /** Used when frequency is MONTHLY. 29-31 fall back to the month's last day in shorter months. */
    dayOfMonth: z.number().int().min(1).max(31),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.enabled && !v.recipient)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recipient'], message: 'A recipient email address is required to turn the reminder on.' });
});
export const DEFAULT_INVOICE_REMINDER_SETTINGS = {
    enabled: false,
    recipient: null,
    frequency: 'DAILY',
    timeOfDay: INVOICE_REMINDER_DEFAULT_TIME,
    weekday: 1,
    dayOfMonth: 1,
};
/** Tolerant reader for the stored JSON (a hand-edited or partial row never throws; it falls back to defaults field by field). */
export function readStoredInvoiceReminderSettings(value) {
    const v = (value ?? {});
    const d = DEFAULT_INVOICE_REMINDER_SETTINGS;
    const frequency = INVOICE_REMINDER_FREQUENCIES.includes(v.frequency) ? v.frequency : d.frequency;
    const int = (x, min, max, fallback) => (typeof x === 'number' && Number.isInteger(x) && x >= min && x <= max ? x : fallback);
    const recipient = typeof v.recipient === 'string' && v.recipient.trim() !== '' ? v.recipient.trim().toLowerCase() : null;
    return {
        enabled: v.enabled === true && recipient !== null,
        recipient,
        frequency,
        timeOfDay: typeof v.timeOfDay === 'string' && TIME_OF_DAY.test(v.timeOfDay) ? v.timeOfDay : d.timeOfDay,
        weekday: int(v.weekday, 1, 7, d.weekday),
        dayOfMonth: int(v.dayOfMonth, 1, 31, d.dayOfMonth),
        orgId: typeof v.orgId === 'string' ? v.orgId : null,
        effectiveFrom: typeof v.effectiveFrom === 'string' ? v.effectiveFrom : null,
    };
}
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** The instant at which the wall clock in `timeZone` reads year-month-day hour:minute. */
export function zonedWallTimeToUtc(year, month, day, hour, minute, timeZone) {
    const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
    let guess = wallAsUtc;
    // Two passes settle the offset (also across a DST change).
    for (let i = 0; i < 2; i++) {
        const p = zonedParts(new Date(guess), timeZone);
        const shownAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
        guess += wallAsUtc - shownAsUtc;
    }
    return new Date(guess);
}
/** True when `schedule` fires on the calendar day year-month-day. Monthly days beyond the month's length fire on its last day. */
export function reminderFiresOn(schedule, year, month, day) {
    if (schedule.frequency === 'DAILY')
        return true;
    if (schedule.frequency === 'WEEKLY') {
        const isoWeekday = ((new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7) + 1;
        return isoWeekday === schedule.weekday;
    }
    return day === Math.min(schedule.dayOfMonth, daysInMonth(year, month));
}
function slotOnDay(schedule, year, month, day, timeZone) {
    if (!reminderFiresOn(schedule, year, month, day))
        return null;
    const [hh, mm] = schedule.timeOfDay.split(':').map(Number);
    return zonedWallTimeToUtc(year, month, day, hh ?? 0, mm ?? 0, timeZone);
}
function shiftDay(y, m, d, delta) {
    const t = new Date(Date.UTC(y, m - 1, d + delta));
    return { year: t.getUTCFullYear(), month: t.getUTCMonth() + 1, day: t.getUTCDate() };
}
/** The most recent scheduled instant at or before `now` (searches back two months), or null. */
export function latestReminderSlotAtOrBefore(schedule, now, timeZone) {
    const today = zonedParts(now, timeZone);
    for (let i = 0; i <= 62; i++) {
        const day = shiftDay(today.year, today.month, today.day, -i);
        const slot = slotOnDay(schedule, day.year, day.month, day.day, timeZone);
        if (slot && slot.getTime() <= now.getTime())
            return slot;
    }
    return null;
}
/** The next scheduled instant strictly after `now` (searches ahead two months), or null. */
export function nextReminderSlotAfter(schedule, now, timeZone) {
    const today = zonedParts(now, timeZone);
    for (let i = 0; i <= 62; i++) {
        const day = shiftDay(today.year, today.month, today.day, i);
        const slot = slotOnDay(schedule, day.year, day.month, day.day, timeZone);
        if (slot && slot.getTime() > now.getTime())
            return slot;
    }
    return null;
}
export const INVOICE_REMINDER_DELIVERY_KINDS = ['SCHEDULED', 'TEST'];
/** Minimum gap between two test sends for one organisation. */
export const INVOICE_REMINDER_TEST_COOLDOWN_SECONDS = 60;
//# sourceMappingURL=invoice-dto.js.map